/**
 * +castux: how the player's cast ended, for the player cast bar (pure, unit-tested):
 *  - `success`: the bar ran out with no cancel / interrupt (a brief bright flash);
 *  - `interrupted`: kicked (red "Interrupted", the locked-out school and its countdown), or
 *    stopped by crowd control (red, no lockout);
 *  - `cancelled`: moved, Esc, target lost / out of range (grey "Cancelled").
 * Death shows nothing (the death box takes over).
 */
import type { SimEvent, UnitId } from '@keystone/sim';

export type CastOutcomeKind = 'success' | 'interrupted' | 'cancelled';

export interface CastOutcomeNote {
  kind: CastOutcomeKind;
  /** Interrupted by a kick: the interrupted ability (its school is locked out). */
  kickedAbilityId: string | null;
}

/** A bar this close to its end when it disappears without an event counts as completed (ms). */
export const SUCCESS_SLACK_MS = 150;
/** Flash / hold lengths (ms, real time). */
export const SUCCESS_FLASH_MS = 380;
export const CANCELLED_SHOW_MS = 750;
export const INTERRUPTED_MIN_MS = 900;
/** The lockout countdown is shown at most this long. */
export const LOCKOUT_SHOW_MAX_MS = 6000;

/** The outcome an event announces for the player's cast, else null. */
export function outcomeFromEvent(e: SimEvent, playerId: UnitId | null): CastOutcomeNote | null {
  if (!playerId) return null;
  if (e.type === 'interrupt' && e.targetID === playerId) return { kind: 'interrupted', kickedAbilityId: e.extraAbilityGameID };
  if (e.type === 'cast_cancelled' && e.sourceID === playerId) {
    switch (e.reason) {
      case 'cc':
        return { kind: 'interrupted', kickedAbilityId: null };
      case 'death':
        return null;
      default:
        return { kind: 'cancelled', kickedAbilityId: null };
    }
  }
  return null;
}

/** A shown cast just disappeared: what to show (the event outcome wins, else success when it ran out). */
export function endedOutcome(pending: CastOutcomeNote | null, endsAt: number, now: number): CastOutcomeKind | null {
  if (pending) return pending.kind;
  return now >= endsAt - SUCCESS_SLACK_MS ? 'success' : null;
}

/** Display name of a school ("nature" → "Nature"). */
export function schoolName(school: string): string {
  return school ? school[0]!.toUpperCase() + school.slice(1) : '';
}

/** The line on the bar for an outcome. */
export function outcomeLabel(kind: CastOutcomeKind, lockedSchool: string | null): string {
  if (kind === 'interrupted') return lockedSchool ? `Interrupted · ${schoolName(lockedSchool)} locked` : 'Interrupted';
  if (kind === 'cancelled') return 'Cancelled';
  return '';
}
